import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { ReadOnlyArea, ReadOnlyBanner } from "@/components/ReadOnly";
import StockCountAndHistory from "@/components/StockCountAndHistory";
import ArticleStockCard from "@/components/ArticleStockCard";
import ArticleReception from "@/components/ArticleReception";
import { fetchAllocationRun, syncAllocations, type AllocationPoint } from "@/services/articleStocks";
import { useFormat } from "@/lib/format";
import { OPERATOR_KEY, readOperator } from "@/services/api";
import { fetchKitMovements } from "@/services/stockLedger";
import { ConfirmModal, QtyControls } from "@/components/QtyEntry";
import DemandChart from "@/components/DemandChart";
import KitChecklists, { CartonSheetsButton } from "@/components/KitChecklists";
import { articleName } from "@/services/articles";
import {
  fetchBoard, createOrder, startOrder, pauseOrder, stopOrder, addUnit, finishOrder, checkItem, validateUnit, cancelOrder, STOP_REASONS,
  takeOver, assignOrder, splitOrder, reopenOrder, resolveStop, saveCapacity, fetchAudit, sendKits, adjustKit, deleteOrder,
  type Board, type Order, type StockRow, type AuditIssue,
} from "@/services/production";

export default function ProductionPage({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const qc = useQueryClient();
  // His board is live: another preparer's ticks and orders appear without a reload (every 30 s here).
  const q = useQuery({ queryKey: ["production", warehouseId], queryFn: () => fetchBoard(warehouseId), refetchInterval: 30_000 });
  const after = (d: Board) => qc.setQueryData(["production", warehouseId], d);

  const m = {
    create: useMutation({ mutationFn: createOrder, onSuccess: after }),
    start: useMutation({ mutationFn: startOrder, onSuccess: after }),
    pause: useMutation({ mutationFn: pauseOrder, onSuccess: after }),
    unit: useMutation({ mutationFn: (v: { id: number; delta: number }) => addUnit(v.id, v.delta), onSuccess: after }),
    finish: useMutation({ mutationFn: finishOrder, onSuccess: after }),
    stop: useMutation({ mutationFn: (v: { id: number; reason: string; detail: string }) => stopOrder(v.id, v.reason, v.detail), onSuccess: after }),
    check: useMutation({ mutationFn: (v: { id: number; ref: string; on: boolean }) => checkItem(v.id, v.ref, v.on), onSuccess: after }),
    validate: useMutation({ mutationFn: validateUnit, onSuccess: after }),
    cancel: useMutation({ mutationFn: cancelOrder, onSuccess: after }),
    takeOver: useMutation({ mutationFn: takeOver, onSuccess: after }),
    assign: useMutation({ mutationFn: (v: { id: number; to: string }) => assignOrder(v.id, v.to), onSuccess: after }),
    split: useMutation({ mutationFn: (v: { id: number; qty: number; to: string }) => splitOrder(v.id, v.qty, v.to), onSuccess: after }),
    reopen: useMutation({ mutationFn: reopenOrder, onSuccess: after }),
    resolve: useMutation({ mutationFn: resolveStop, onSuccess: after }),
    remove: useMutation({ mutationFn: deleteOrder, onSuccess: after }),
  };
  // His capacity fields save as they change. Saves go one after the other, so the latest values win
  // even when people and hours are changed a moment apart.
  const capQueue = useRef<Promise<unknown>>(Promise.resolve());
  const [capSaved, setCapSaved] = useState(false);
  const [capFailed, setCapFailed] = useState(false);
  const saveCap = (people: number, hours: number) => {
    capQueue.current = capQueue.current.then(() => saveCapacity(people, hours))
      .then((d) => { after(d as Board); setCapSaved(true); setCapFailed(false); })
      .catch(() => { setCapSaved(false); setCapFailed(true); });
  };
  const { staff } = useAuth();
  // His two views, chosen per device: the manager's (every card) and the preparer's (the queue, what
  // is finished, the stock in place). ?vue=preparateur locks this device to the preparer's view;
  // ?vue=responsable lifts the lock.
  const [view, setViewState] = useState<"resp" | "prep">(() => initialView());
  const locked = viewLocked();
  const setView = (v: "resp" | "prep") => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* this tab only */ } };
  const resp = view === "resp";
  // His "Vous êtes :": the first name of whoever is at this shared tablet.
  const [operator, setOperatorState] = useState(readOperator);
  const setOperator = (v: string) => { setOperatorState(v); try { localStorage.setItem(OPERATOR_KEY, v.trim()); } catch { /* this tab only */ } };
  // His "Préparer un bon ×N": a shortage line fills the new-order form.
  const [prefill, setPrefill] = useState<{ code: string; qty: number; n: number } | null>(null);
  // His "Préparer un envoi ×N": a Dielsdorf / Berlin shortage fills the shipment card.
  const [sendPrefill, setSendPrefill] = useState<{ to: string; code: string; qty: number; n: number } | null>(null);
  // The server's own name for me (worker_name): who owns an order I start.
  const me = operator.trim() || staff?.name || staff?.email || "";
  const busy = m.check.isPending || m.validate.isPending || m.unit.isPending || m.finish.isPending;
  const failed = (m.check.error || m.validate.error || m.unit.error || m.finish.error || m.start.error || m.takeOver.error
    || m.split.error || m.assign.error || m.cancel.error || m.reopen.error || m.pause.error || m.stop.error || m.resolve.error || m.remove.error) as Error | null;

  if (q.isPending) return <p className="text-sm text-slate-400">{t("loading")}</p>;
  // A board already on screen stays when a later refresh fails; the chip says it is not in sync.
  if (q.error && !q.data) return <Problem message={(q.error as Error).message} />;
  const b = q.data!;
  // His sync chip: in sync, not in sync (the board could not be refreshed), or a save that failed.
  const sync = failed || capFailed || m.create.isError ? "failed" : q.isRefetchError ? "off" : "ok";

  const orderRow = (o: Order) => (
    <OrderRow key={o.id} order={o} busy={busy} me={me} manager={resp} onDelete={() => m.remove.mutate(o.id)}
      onTakeOver={() => m.takeOver.mutate(o.id)}
      onAssign={(to) => m.assign.mutate({ id: o.id, to })}
      onSplit={(qty, to) => m.split.mutate({ id: o.id, qty, to })}
      onStart={() => m.start.mutate(o.id)} onPause={() => m.pause.mutate(o.id)}
      onUnit={(d) => m.unit.mutate({ id: o.id, delta: d })} onFinish={() => m.finish.mutate(o.id)}
      onCheck={(ref, on) => m.check.mutate({ id: o.id, ref, on })} onValidate={() => m.validate.mutate(o.id)}
      onCancel={() => m.cancel.mutate(o.id)}
      onStop={(reason, detail) => m.stop.mutate({ id: o.id, reason, detail })} />
  );

  return (
    <div className="space-y-6">
      {/* His viewer level: every write below sits in a ReadOnlyArea; reading, the history, the sheets
          and printing stay open to a reader. The server refuses the writes anyway (ModuleGate). */}
      <ReadOnlyBanner module="production" text={t("prod.readOnly")} />
      <DevBanner />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* His role switch; hidden on a device locked to the preparer's view. */}
        {!locked ? (
          <div className="flex gap-1">
            {(["resp", "prep"] as const).map((v) => (
              <button key={v} onClick={() => setView(v)} aria-pressed={view === v}
                className={`rounded-lg px-3 py-1 text-sm ${view === v ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
                {t(`prod.view.${v}`)}
              </button>
            ))}
          </div>
        ) : <span />}
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${
          sync === "ok" ? "bg-emerald-50 text-emerald-800" : sync === "off" ? "bg-slate-100 text-slate-600" : "bg-red-50 text-red-800"}`}>
          {t(`prod.sync.${sync}`)}
        </span>
      </div>
      <datalist id="prepNamesList">{b.known_names.map((n) => <option key={n} value={n} />)}</datalist>

      {resp && (
        <>
          <Progress board={b} />
          {capFailed && <p className="text-sm text-red-700">{t("prod.sync.failed")}</p>}
          {capSaved && (
            <p className="text-sm text-emerald-700">✅ {t("prod.capacitySaved").replace("{p}", String(b.progress.capacity.people)).replace("{h}", String(b.progress.capacity.hours_per_day))}</p>
          )}
          <DemandChart warehouseId={warehouseId} />
          <PreparerTable rows={b.by_preparer} />
          {b.stopped.length > 0 && <ReadOnlyArea module="production"><StoppedBox stops={b.stopped} onResolve={(id) => m.resolve.mutate(id)} /></ReadOnlyArea>}
          <AuditCard boardAt={q.dataUpdatedAt} />
          <ReadOnlyArea module="production">
            <NewOrder kits={b.stock} warehouseId={warehouseId} capacity={b.progress.capacity} articleStock={b.article_stock ?? {}} prefill={prefill}
              onCreate={(v) => m.create.mutateAsync(v)} onCapacity={saveCap} />
          </ReadOnlyArea>
          {/* His "Bons de production": every order, newest first. */}
          <Card title={t("prod.orders")}>
            <p className="px-5 pb-2 text-xs text-slate-500">{t("prod.ordersHint")}</p>
            {(b.orders ?? []).length === 0 ? <p className="px-5 py-3 text-sm text-slate-400">{t("prod.ordersEmpty")}</p> : (
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {failed && <li className="px-5 py-2 text-sm text-red-700">{failed.message}</li>}
                {(b.orders ?? []).map(orderRow)}
              </ul>
            )}
          </Card>
        </>
      )}

      {!resp && (
        <>
          <ReadOnlyArea module="production"><ArticleReception warehouseId={warehouseId} /></ReadOnlyArea>
          {/* His preparer's queue: oldest first, behind "You are". */}
          <Card title={t("prod.queue")}>
            <p className="px-5 pb-1 text-xs text-slate-500">{t("prod.queueHint")}</p>
            <label className="flex flex-wrap items-center gap-2 px-5 py-2 text-sm">
              <b className="text-slate-700">{t("prod.youAre")}</b>
              <input value={operator} onChange={(e) => setOperator(e.target.value)} list="prepNamesList" maxLength={40}
                placeholder={t("prod.youArePh")} className="w-64 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            </label>
            {!operator.trim() && <p className="mx-5 mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">{t("prod.nameFirst")}</p>}
            {b.queue.length === 0 ? (
              <p className="px-5 py-3 text-sm text-slate-400">{t("prod.noQueue")}</p>
            ) : (
              <fieldset disabled={!operator.trim()} className="min-w-0">
                <ul className="divide-y divide-slate-100 border-t border-slate-100">
                  {failed && <li className="px-5 py-2 text-sm text-red-700">{failed.message}</li>}
                  {b.queue.map(orderRow)}
                </ul>
              </fieldset>
            )}
          </Card>
          {/* His "Terminés": the last 12 finished orders, newest first, each with "Reopen". */}
          {b.finished.length > 0 && (
            <Card title={t("prod.finishedToday")}>
              <ReadOnlyArea module="production">
              <ul className="divide-y divide-slate-100">
                {b.finished.map((o) => (
                  <li key={o.id} className="flex flex-wrap items-baseline gap-x-4 px-5 py-2.5 text-sm">
                    <span className="font-medium text-slate-800">{o.kit_label || o.kit_code} ×{o.qty}</span>
                    <StatusBadge order={o} />
                    <span className="text-slate-600">{o.done_count}/{o.qty}</span>
                    <span className="text-xs text-slate-400">
                      {Object.entries(o.minutes_by_person).map(([w, mm]) => `${w} ${mm}${t("run.minutes")}`).join(" · ")}
                    </span>
                    <button onClick={() => m.reopen.mutate(o.id)} disabled={m.reopen.isPending}
                      className="ml-auto rounded border border-slate-300 px-2.5 py-1 text-xs">
                      {o.done_count < o.qty ? t("prod.reopenPartial").replace("{d}", String(o.done_count)).replace("{q}", String(o.qty)) : t("prod.reopen")}
                    </button>
                    <div className="basis-full"><OrderMeta order={o} /><TimeCompare order={o} /></div>
                  </li>
                ))}
              </ul>
              </ReadOnlyArea>
            </Card>
          )}
        </>
      )}

      <KitStockCard stock={b.stock} title={resp ? t("prod.stock") : t("prod.stockInPlace")} onSaved={after} />
      {resp && <ReadOnlyArea module="production"><TransferCard stock={b.stock} log={b.last_sends ?? []} prefill={sendPrefill} onSent={after} /></ReadOnlyArea>}

      <StockCountAndHistory warehouseId={warehouseId} stock={b.stock} />
      {resp && <ReadOnlyArea module="production"><ArticleReception warehouseId={warehouseId} manager /></ReadOnlyArea>}
      <ArticleStockCard warehouseId={warehouseId} />

      <Card title={t("prod.allocations")}>
        {/* The preparer sees what is reserved; the manager also runs the check and acts on shortages. */}
        {resp && (
          <ReadOnlyArea module="production">
            <AllocationSync />
            <Shortages stock={b.stock} onOrder={(code, qty) => setPrefill({ code, qty, n: Date.now() })}
              onTransfer={(to, code, qty) => setSendPrefill({ to, code, qty, n: Date.now() })} />
          </ReadOnlyArea>
        )}
        <AllocationList allocations={b.allocations} stock={b.stock} />
      </Card>

      <DocumentsCard />
    </div>
  );
}

const VIEW_KEY = "myotaku-warehouse:prod-view";
const VIEW_LOCK_KEY = "myotaku-warehouse:prod-view-locked";
// His `lockToPreparateurIfLinked`: the link decides first, then this device's lock, then its choice.
function initialView(): "resp" | "prep" {
  try {
    const vue = new URLSearchParams(window.location.search).get("vue");
    if (vue === "preparateur") { localStorage.setItem(VIEW_LOCK_KEY, "1"); localStorage.setItem(VIEW_KEY, "prep"); return "prep"; }
    if (vue === "responsable") { localStorage.removeItem(VIEW_LOCK_KEY); localStorage.setItem(VIEW_KEY, "resp"); return "resp"; }
    if (localStorage.getItem(VIEW_LOCK_KEY) === "1") return "prep";
    return localStorage.getItem(VIEW_KEY) === "prep" ? "prep" : "resp";
  } catch { return "resp"; }
}
function viewLocked() {
  try { return localStorage.getItem(VIEW_LOCK_KEY) === "1"; } catch { return false; }
}

// His "Suivi par préparateur" for today.
function PreparerTable({ rows }: { rows: Board["by_preparer"] }) {
  const { t } = useT();
  return (
    <Card title={t("prod.byPreparer")}>
      {rows.length === 0 ? <p className="px-5 py-3 text-sm text-slate-400">{t("prod.prep.none")}</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="px-5 py-2 text-left font-medium">{t("prod.prep.who")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.kits")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.worked")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.given")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.gap")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.avg")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("prod.prep.pauses")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("prod.prep.now")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const gap = p.worked_minutes - p.given_minutes;
                return (
                  <tr key={p.name ?? "—"} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">{p.name ?? t("prod.prep.noName")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.kits_made}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{hm(p.worked_minutes)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.kits_made ? hm(p.given_minutes) : "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${p.kits_made ? (gap > 0 ? "text-red-700" : "text-emerald-700") : ""}`}>
                      {p.kits_made ? `${gap > 0 ? "+" : "−"}${hm(Math.abs(gap))}` : "—"}
                    </td>
                    {/* nil, not 0 — "no rate yet" and "a rate of zero" are different things. */}
                    <td className="px-3 py-2 text-right tabular-nums">{p.minutes_per_kit != null ? hm(p.minutes_per_kit) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.pauses}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {p.now.length ? p.now.map((n) => n.status === "paused"
                        ? t("prod.prep.nowPaused", { k: n.kit_label, q: n.qty, t: n.paused_at ? clock(n.paused_at) : "—" })
                        : t("prod.prep.nowRunning", { k: n.kit_label, q: n.qty, d: n.done_count })).join(" · ") : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// His "Allocation kits clients", on demand: reads every approved delivery from the feed and
// allocates its kits by his rules; a delivery the crew has ticked leaves stock once.
function AllocationSync() {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const qc = useQueryClient();
  const last = useQuery({ queryKey: ["allocation-run"], queryFn: fetchAllocationRun, refetchInterval: 60_000 });
  const run = useMutation({ mutationFn: syncAllocations, onSuccess: () => {
    ["production", "stock-movements", "article-stocks", "allocation-run"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
  } });
  const r = run.data;
  const lr = last.data?.last_run;
  // His stale rule: more than 26 h without a check (70 h over the weekend and on Monday).
  const day = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "Europe/Zurich" }).format(new Date());
  const ageH = lr ? (Date.now() - new Date(lr.ran_at).getTime()) / 3_600_000 : 0;
  const stale = !!lr && (!!lr.error || ageH > (["Sat", "Sun", "Mon"].includes(day) ? 70 : 26));
  return (
    <>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 px-5 py-2 text-xs text-slate-500">
      <span>{t("alloc.schedule")}</span>
      {lr
        ? <span className={stale ? "font-medium text-red-600" : ""}>
            · {t("alloc.lastRun")} {zurichDateTime(lr.ran_at)} ({lr.trigger === "schedule" ? t("alloc.auto") : lr.by})
            {lr.error ? ` — ${t("alloc.incomplete")} : ${lr.error}` : stale ? ` — ${t("alloc.stale")}` : ""}
          </span>
        : last.data && <span>· {t("alloc.never")}</span>}
      {last.data?.next_runs?.[0] && <span>· {t("alloc.next")} {zurichDateTime(last.data.next_runs[0])}</span>}
    </div>
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-2 text-sm">
      <button onClick={() => run.mutate()} disabled={run.isPending} className="rounded-lg bg-slate-900 px-3 py-1.5 text-white disabled:opacity-40">
        {run.isPending ? "…" : t("alloc.sync")}
      </button>
      {r && <span className="text-slate-600">{t("alloc.result").replace("{q}", String(r.qualified)).replace("{c}", String(r.created))
        .replace("{u}", String(r.updated)).replace("{r}", String(r.released)).replace("{d}", String(r.delivered))}</span>}
      {r && r.unknown.length > 0 && <span className="text-amber-700">{t("alloc.unknown")}: {r.unknown.slice(0, 3).join(" · ")}</span>}
      {run.error && <span className="text-red-600">{(run.error as Error).message}</span>}
    </div>
    <AllocationPoints points={lr?.outcome?.points ?? []} />
    </>
  );
}

// His "Points à vérifier" of the last run, in his words (Ludovic's task A15, rule 7 bis and steps
// 4-5b, answers of 03.11.2026): a delivery ticked partly whose kits all left, one not yet ticked, one
// still reserved after its day, one moved after its kits left, an exit on too little stock.
function AllocationPoints({ points }: { points: AllocationPoint[] }) {
  const { t } = useT();
  if (!points.length) return null;
  const ddmm = (d?: string | null) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : "—");
  const wh = (id: number | null) => KIT_WH.find((w) => Number(w.id) === id)?.name ?? (id ? `#${id}` : "—");
  const text = (p: AllocationPoint) => {
    const v = { date: ddmm(p.day), wh: wh(p.warehouse_id), contract: p.contract_id ?? "—", client: p.client ?? "" };
    switch (p.kind) {
      case "partial": return t("alloc.point.partial", { ...v, n: p.done, total: p.total });
      case "not_ticked": return t("alloc.point.notTicked", v);
      case "still_reserved": return t("alloc.point.stillReserved", { ...v, reason: t(`alloc.point.reason.${p.reason ?? "missing"}`) });
      case "postponed": return t("alloc.point.postponed", { ...v, newDate: ddmm(p.new_day) });
      case "short": return p.article
        ? t("alloc.point.shortArticle", { ...v, code: p.article, before: p.before, qty: p.qty })
        : t("alloc.point.short", { ...v, code: p.kit, before: p.before, qty: p.qty });
    }
  };
  return (
    <div className="border-b border-slate-100 px-5 py-2 text-xs">
      <p className="font-semibold text-amber-800">{t("alloc.points")} ({points.length})</p>
      <ul className="mt-1 space-y-0.5 text-slate-700">
        {points.map((p, i) => <li key={`${p.kind}-${p.event_id}-${p.kit ?? p.article ?? ""}-${i}`}>{text(p)}</li>)}
      </ul>
    </div>
  );
}

function Progress({ board }: { board: Board }) {
  const { t } = useT();
  const p = board.progress;
  const ahead = p.variance_minutes <= 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
        <h2 className="text-sm font-semibold text-slate-900">{t("prod.progress")}</h2>
        <span className="text-sm text-slate-600"><b className="text-slate-900">{p.done_kits}</b> / {p.target_kits} {t("prod.target")}</span>
        <span className="text-sm text-slate-600">{p.worked_minutes} / {p.estimated_minutes} {t("run.minutes")}</span>
        <span className={`text-sm font-medium ${ahead ? "text-emerald-700" : "text-amber-700"}`}>
          {Math.abs(p.variance_minutes)} {t("run.minutes")} {ahead ? t("prod.ahead") : t("prod.behind")}
        </span>
        <span className="ml-auto text-2xl font-semibold tabular-nums text-slate-900">{p.percent}%</span>
      </div>
      {/* His four tiles: objective, total time required, the day's capacity, and the margin left — or the overrun. */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={t("prod.objective")} value={`${p.target_kits} ${p.target_kits === 1 ? "kit" : "kits"}`} />
        <Tile label={t("prod.timeRequired")} value={hm(p.estimated_minutes)} />
        <Tile label={t("prod.capacityDay")} value={`${hm(p.capacity_minutes)} (${p.capacity.people}×${p.capacity.hours_per_day}h)`} />
        <Tile label={t("prod.marginLeft")} tone={p.margin_minutes >= 0 ? "good" : "bad"}
          value={p.margin_minutes >= 0 ? hm(p.margin_minutes) : `${t("prod.overrun")} ${hm(-p.margin_minutes)}`} />
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-600 transition-all"
          style={{ width: `${Math.min(100, p.percent)}%` }} />
      </div>
    </div>
  );
}

// His fmtHM: 125 → "2h05".
function hm(minutes: number) {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  const c = tone === "good" ? "bg-emerald-50 text-emerald-800" : tone === "bad" ? "bg-red-50 text-red-800" : "bg-slate-50 text-slate-800";
  return (
    <div className={`rounded-lg px-3 py-2 ${c}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function NewOrder({ kits, warehouseId, capacity, articleStock, prefill, onCreate, onCapacity }: {
  kits: StockRow[]; warehouseId: number | null; prefill: { code: string; qty: number; n: number } | null;
  capacity: { people: number; hours_per_day: number };
  articleStock: Record<string, number>;
  onCreate: (v: { kit_code: string; warehouse_id: number; qty: number; assigned_to?: string; note?: string }) => Promise<unknown>;
  onCapacity: (people: number, hours: number) => void;
}) {
  const { t, locale } = useT();
  // His composer: no kit chosen until one is tapped, one unit by default.
  const [code, setCode] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState<null | "sending" | "failed">(null);
  const [people, setPeople] = useState(String(capacity.people));
  const [hours, setHours] = useState(String(capacity.hours_per_day));
  // His capacity fields save on change; a comma is accepted for the half hour.
  const saveCap = (pp: string, hh: string) => {
    const n = Number(pp), h = Number(hh.replace(",", "."));
    if (n >= 1 && n <= 50 && h >= 1 && h <= 24 && Number.isInteger(h * 2) && (n !== capacity.people || h !== capacity.hours_per_day)) onCapacity(n, h);
  };
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!prefill) return;
    setCode(prefill.code); setQty(Math.min(999, Math.max(1, prefill.qty)));
    box.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [prefill]);
  const kit = kits.find((k) => k.code === code) ?? null;
  const byCode = new Map(kits.map((k) => [k.code, k]));
  const clampQty = (n: number) => Math.min(999, Math.max(1, Math.round(n) || 1));
  // His `computeShortages`: what one unit takes × the quantity, against Penthalaz's articles.
  const shortages = kit ? Object.entries(kit.per_unit ?? {}).map(([c, per]) => ({ code: c, missing: per * qty - (articleStock[c] ?? 0) }))
    .filter((s) => s.missing > 0) : [];
  // His orders are made at Penthalaz; a chosen warehouse is used, "All" means Penthalaz.
  const target = warehouseId ?? 1;
  const send = () => {
    if (!kit || sending) return;
    setSending("sending");
    onCreate({ kit_code: kit.code, warehouse_id: target, qty, assigned_to: assignee.trim() || undefined, note: note.trim() || undefined })
      .then(() => { setAssignee(""); setNote(""); setQty(1); setCode(null); setTimeout(() => setSending(null), 900); })
      .catch(() => { setSending("failed"); setTimeout(() => setSending(null), 4000); });
  };

  return (
    <div ref={box}>
    <Card title={t("prod.newOrder")}>
      <div className="space-y-3 px-5 py-3">
        <p className="text-xs text-slate-500">{t("prod.newOrderHint")}</p>
        <span className="block text-xs uppercase tracking-wide text-slate-500">{t("prod.kit")}</span>
        {/* His kit grid: a chip per kit in its colour, its time, its sub-kits and Penthalaz's figures. */}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {kits.map((k) => {
            const pz = k.penthalaz;
            const colour = k.colour ?? "#64748b";
            return (
              <button key={k.code} onClick={() => setCode(k.code)} aria-pressed={code === k.code}
                className={`rounded-xl border-2 px-3 py-2 text-left text-xs transition ${code === k.code ? "shadow-md" : "border-slate-200 hover:border-slate-300"}`}
                style={code === k.code ? { borderColor: colour, background: `${colour}14` } : undefined}>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colour }} />{k.label || k.code}
                </span>
                {k.unit_minutes != null && <span className="block text-slate-500">{t("prod.chip.min", { n: k.unit_minutes })}</span>}
                {(k.composed_of?.length ?? 0) > 0 && <span className="block text-indigo-700">{t("prod.chip.composed", { n: k.composed_of!.length })}</span>}
                <span className="block text-slate-600">{t("prod.chip.inStock", { n: pz?.on_hand ?? k.on_hand })}</span>
                {pz && pz.need > 0 && (
                  <span className={`mt-0.5 inline-block rounded px-1.5 font-medium ${pz.available < 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {pz.available < 0 ? t("prod.chip.short", { n: -pz.available, r: pz.need }) : t("prod.chip.dispo", { n: pz.available, r: pz.need })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* His "Ce kit comprend": a MUSA kit's sub-kits, each in its colour with its minutes. */}
        {kit && (kit.composed_of?.length ?? 0) > 0 && (
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="mb-1 text-xs font-semibold text-slate-600">{t("prod.contains")}</p>
            <div className="flex flex-wrap gap-1.5">
              {kit.composed_of!.map((c) => {
                const sub = byCode.get(c);
                return (
                  <span key={c} className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-slate-200">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: sub?.colour ?? "#888" }} />
                    {sub?.label || c}{sub?.unit_minutes != null && <span className="text-slate-400">{t("prod.chip.minShort", { n: sub.unit_minutes })}</span>}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-500">{t("prod.qty")}</span>
          <button onClick={() => setQty((q) => clampQty(q - 1))} className="h-9 w-9 rounded-lg border border-slate-300 text-lg">−</button>
          <input type="number" min={1} max={999} value={qty} onChange={(e) => setQty(clampQty(Number(e.target.value)))}
            className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-center font-mono text-sm" />
          <button onClick={() => setQty((q) => clampQty(q + 1))} className="h-9 w-9 rounded-lg border border-slate-300 text-lg">+</button>
          <span className="text-sm text-slate-600">
            {kit && kit.unit_minutes != null ? <>{t("prod.qtyPreview", { q: qty, m: kit.unit_minutes })} <b>{hm(qty * kit.unit_minutes)}</b></> : t("prod.pickKit")}
          </span>
        </div>
        {kit && shortages.length > 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
            {t("prod.shortage", {
              q: qty, k: kit.label || kit.code,
              list: shortages.slice(0, 4).map((s) => `${s.missing} × ${s.code} (${articleName(s.code, locale) ?? s.code})`).join(", "),
              more: shortages.length > 4 ? t("prod.shortageMore", { n: shortages.length - 4 }) : "",
            })}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wide text-slate-500">{t("prod.note")}</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} rows={2} placeholder={t("prod.notePh")}
              className="w-72 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wide text-slate-500">{t("prod.assignTo")}</span>
            <input value={assignee} onChange={(e) => setAssignee(e.target.value)} list="prepNamesList" maxLength={40}
              placeholder={t("prod.assignPh")} className="w-56 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <button disabled={!kit || sending !== null} onClick={send}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${sending === "failed" ? "bg-red-700" : "bg-slate-900"}`}>
            {sending === "sending" ? t("prod.sending") : sending === "failed" ? t("prod.sendFailed") : t("prod.sendOrder")}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 px-5 py-3">
        <span className="self-center text-xs font-semibold uppercase tracking-wide text-slate-500">{t("prod.teamCapacity")}</span>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-slate-500">{t("prod.people")}</span>
          <input type="number" min={1} max={50} value={people} onChange={(e) => setPeople(e.target.value)}
            onBlur={() => saveCap(people, hours)} className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-slate-500">{t("prod.hoursPerDay")}</span>
          <input inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)}
            onBlur={() => saveCap(people, hours)} className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </label>
      </div>
    </Card>
    </div>
  );
}

// His warehouses for kits, and the view of all three together.
const KIT_WH = [{ id: "1", name: "Penthalaz" }, { id: "67", name: "Dielsdorf" }, { id: "4", name: "Berlin" }] as const;
const STOCK_VIEW_KEY = "myotaku-warehouse:kit-stock-view";

// His kit stock card: Penthalaz / Dielsdorf / Berlin / Total, remembered on this device; each kit with
// what is allocated, what Penthalaz must send to the others, and what is left or short.
function KitStockCard({ stock, title, onSaved }: { stock: StockRow[]; title: string; onSaved: (b: Board) => void }) {
  const { t } = useT();
  // His entry mode for kits, remembered on the device: add a quantity, or correct the total.
  const [mode, setModeState] = useState<"add" | "set">(() => { try { return localStorage.getItem(ENTRY_KEY) === "set" ? "set" : "add"; } catch { return "add"; } });
  const setMode = (v: "add" | "set") => { setModeState(v); try { localStorage.setItem(ENTRY_KEY, v); } catch { /* this tab only */ } };
  const [ask, setAsk] = useState<null | { kit: StockRow; prev: number; next: number; send: () => Promise<Board> }>(null);
  const [toast, setToast] = useState<null | { ok: boolean; detail: string }>(null);
  const [history, setHistory] = useState<StockRow | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), toast.ok ? 3500 : 8000);
    return () => clearTimeout(id);
  }, [toast]);
  const kitLabel = (k: StockRow, w: string) => `${k.label || k.code} (${KIT_WH.find((x) => x.id === w)?.name ?? ""})`;
  const detail = (label: string, prev: number, next: number) => `${label} : ${prev} ${next >= prev ? "+" : "−"} ${Math.abs(next - prev)} = ${next}`;
  // His `commit`: work out the new figure, ask, then write it and say whether it was saved.
  const commit = (k: StockRow, w: string, op: "delta" | "set", n: number, plusMinus = false) => {
    const prev = k.warehouses?.[w]?.qty ?? 0;
    const next = Math.max(0, Math.round(op === "delta" ? prev + n : n));
    if (next === prev) return;
    setAsk({ kit: k, prev, next, send: () => adjustKit(op === "set" ? { kit_code: k.code, warehouse_id: Number(w), set: next }
      : { kit_code: k.code, warehouse_id: Number(w), delta: n, mode: plusMinus ? "plus_minus" : undefined }) });
  };
  const [view, setViewState] = useState<string>(() => { try { return localStorage.getItem(STOCK_VIEW_KEY) || "1"; } catch { return "1"; } });
  const setView = (v: string) => { setViewState(v); try { localStorage.setItem(STOCK_VIEW_KEY, v); } catch { /* this tab only */ } };
  const at = (k: StockRow, w: string) => k.warehouses?.[w] ?? { qty: 0, allocated: 0 };
  const sums = Object.fromEntries(KIT_WH.map((w) => [w.id, stock.reduce((s, k) => s + at(k, w.id).qty, 0)]));
  const total = view === "total" ? KIT_WH.reduce((s, w) => s + sums[w.id], 0) : sums[view] ?? 0;
  const name = KIT_WH.find((w) => w.id === view)?.name;
  return (
    <Card title={title}>
      <div className="flex flex-wrap items-center gap-1 px-5 pt-1">
        {[...KIT_WH.map((w) => ({ id: w.id as string, label: w.name as string })), { id: "total", label: t("prod.stockTotalTab") }].map((w) => (
          <button key={w.id} onClick={() => setView(w.id)} aria-pressed={view === w.id}
            className={`rounded-lg px-3 py-1 text-sm ${view === w.id ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{w.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 px-5 py-2">
        <span className="text-sm text-slate-600">{view === "total" ? t("prod.stockTotal") : t("prod.stockAt", { w: name ?? "" })}</span>
        <b className="text-lg tabular-nums text-slate-900">{t(total === 1 ? "prod.kitsOne" : "prod.kitsMany", { n: total })}</b>
        <span className="text-xs text-slate-400">{KIT_WH.map((w) => `${w.name} ${sums[w.id]}`).join(" · ")}</span>
      </div>
      <p className="px-5 pb-2 text-xs text-slate-500">{t("prod.reservedNote")}</p>
      {view !== "total" && (
        <div className="px-5 pb-2">
          <ReadOnlyArea module="production">
          <div className="flex flex-wrap items-center gap-1 text-sm">
            <span className="mr-1 text-xs text-slate-500">{t("prod.entry.label")}</span>
            {(["add", "set"] as const).map((v) => (
              <button key={v} onClick={() => setMode(v)} aria-pressed={mode === v}
                className={`rounded-lg px-2.5 py-1 text-xs ${mode === v ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200"}`}>{t(`prod.entry.${v}`)}</button>
            ))}
          </div>
          </ReadOnlyArea>
          <p className="mt-1 text-xs text-slate-500">{t(`prod.entry.${mode}Help`)}</p>
        </div>
      )}
      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {stock.map((k) => {
          const qty = view === "total" ? KIT_WH.reduce((s, w) => s + at(k, w.id).qty, 0) : at(k, view).qty;
          const allocated = view === "total" ? KIT_WH.reduce((s, w) => s + at(k, w.id).allocated, 0) : at(k, view).allocated;
          const toSend = view === "1" ? (k.penthalaz?.to_send ?? 0) : 0;
          const left = qty - allocated - toSend;
          return (
            <li key={k.code} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2 text-sm">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: k.colour ?? "#888" }} />
              {/* His name opens the kit's history. */}
              <button onClick={() => setHistory(k)} title={t("prod.history.open")} className="font-medium text-slate-800 underline decoration-dotted underline-offset-2">{k.label || k.code}</button>
              {view === "total" && <span className="text-xs text-slate-400">{KIT_WH.map((w) => `${w.name[0]} ${at(k, w.id).qty}`).join(" · ")}</span>}
              {(allocated > 0 || toSend > 0) && (
                <span className="flex flex-wrap gap-1 text-xs">
                  {allocated > 0 && <span className="rounded bg-amber-50 px-1.5 text-amber-800" title={t("prod.pill.allocatedT")}>{t("prod.pill.allocated", { n: allocated })}</span>}
                  {toSend > 0 && <span className="rounded bg-sky-50 px-1.5 text-sky-800" title={t("prod.pill.toSendT")}>{t("prod.pill.toSend", { n: toSend })}</span>}
                  <span className={`rounded px-1.5 font-medium ${left < 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {left < 0 ? t("prod.pill.short", { n: -left }) : t("prod.pill.dispo", { n: left })}
                  </span>
                </span>
              )}
              {view === "total" ? <span className="ml-auto font-mono text-base font-semibold tabular-nums text-slate-900">{qty}</span>
                : <div className="ml-auto"><ReadOnlyArea module="production"><QtyControls mode={mode} qty={qty} label={k.label || k.code}
                    onDelta={(n, pm) => commit(k, view, "delta", n, pm)} onSet={(n) => commit(k, view, "set", n)} /></ReadOnlyArea></div>}
            </li>
          );
        })}
      </ul>
      {ask && (
        <ConfirmModal down={ask.next < ask.prev} yes={t(ask.next > ask.prev ? "prod.entry.yesAdd" : "prod.entry.yesDown")}
          question={t(ask.next > ask.prev ? "prod.entry.askAdd" : "prod.entry.askDown", { n: Math.abs(ask.next - ask.prev), k: kitLabel(ask.kit, view) })}
          detail={detail(kitLabel(ask.kit, view), ask.prev, ask.next)}
          onNo={() => setAsk(null)}
          onYes={() => {
            const a = ask; setAsk(null);
            const d = detail(kitLabel(a.kit, view), a.prev, a.next);
            a.send().then((b) => { onSaved(b); setToast({ ok: true, detail: d }); }, () => setToast({ ok: false, detail: d }));
          }} />
      )}
      {toast && (
        <button onClick={() => setToast(null)} role="status"
          className={`fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2.5 text-left text-sm text-white shadow-lg ${toast.ok ? "bg-emerald-700" : "bg-red-700"}`}>
          {t(toast.ok ? "prod.entry.saved" : "prod.entry.notSaved")}
          <span className="block text-xs opacity-90">{toast.detail}</span>
          <span className="block text-[11px] opacity-70">{t("prod.entry.tapClose")}</span>
        </button>
      )}
      {history && <KitHistory kit={history} warehouse={view === "total" ? null : Number(view)} current={view === "total" ? KIT_WH.reduce((s, w) => s + at(history, w.id).qty, 0) : at(history, view).qty} onClose={() => setHistory(null)} />}
    </Card>
  );
}

const ENTRY_KEY = "myotaku-warehouse:kit-entry";

// His `showKitHistory`: one kit's movements — when, what, +/−, before → after, by whom (a delivery
// with its client and contract) — for this warehouse or all, with the current stock.
function KitHistory({ kit, warehouse, current, onClose }: { kit: StockRow; warehouse: number | null; current: number; onClose: () => void }) {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const q = useQuery({ queryKey: ["kit-history", kit.code, warehouse], queryFn: () => fetchKitMovements(kit.code, warehouse) });
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  const whName = (id: number) => KIT_WH.find((w) => Number(w.id) === id)?.name ?? `#${id}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-5 py-3">
          <h3 className="font-semibold text-slate-900">{t("prod.history.title", { k: kit.label || kit.code, w: warehouse ? whName(warehouse) : t("prod.stockTotalTab") })}</h3>
          <button onClick={onClose} className="text-sm underline">{t("prod.history.close")}</button>
        </div>
        <p className="px-5 py-2 text-sm text-slate-600">{t("prod.history.current", { n: current })}</p>
        <ul className="flex-1 divide-y divide-slate-100 overflow-y-auto px-5 pb-3 text-sm">
          {q.isPending && <li className="py-2 text-slate-400">{t("loading")}</li>}
          {q.data?.movements.length === 0 && <li className="py-2 text-slate-400">{t("prod.history.none")}</li>}
          {q.data?.movements.map((m) => (
            <li key={m.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5">
              <span className="w-28 shrink-0 text-xs text-slate-500">{zurichDateTime(m.at)}</span>
              <span className="text-slate-700">{t(`inv.src.${m.source}`)}{!warehouse ? ` · ${whName(m.myotaku_warehouse_id)}` : ""}
                {m.source === "delivery" && (m.client_name || m.contract_id) ? ` — ${[m.client_name, m.contract_id ? `#${m.contract_id}` : null].filter(Boolean).join(" · ")}` : ""}</span>
              <b className={`tabular-nums ${m.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>{m.delta >= 0 ? "+" : "−"}{Math.abs(m.delta)}</b>
              <span className="font-mono text-xs text-slate-500">{m.previous_qty} → {m.qty}</span>
              {m.by_name && <span className="ml-auto text-xs text-slate-400">{m.by_name}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// His "Envoi de kits vers un entrepôt": choose Dielsdorf or Berlin, type how many of each kit leave
// Penthalaz, a note, then send — never more than Penthalaz holds. The kits are in transit until the
// destination receives them (Receiving).
function TransferCard({ stock, log, prefill, onSent }: {
  stock: StockRow[]; log: NonNullable<Board["last_sends"]>; prefill: { to: string; code: string; qty: number; n: number } | null;
  onSent: (b: Board) => void;
}) {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const [to, setTo] = useState("67");
  const [qty, setQty] = useState<Record<string, number>>({});
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!prefill) return;
    setTo(prefill.to); setQty({ [prefill.code]: prefill.qty }); setMsg(null);
    box.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [prefill]);
  const dest = KIT_WH.find((w) => w.id === to)!.name;
  const at = (k: StockRow, w: string) => k.warehouses?.[w]?.qty ?? 0;
  const total = Object.values(qty).reduce((s, n) => s + n, 0);
  const over = stock.filter((k) => (qty[k.code] ?? 0) > at(k, "1"));
  const label = (code: string) => stock.find((k) => k.code === code)?.label || code;
  const send = useMutation({
    mutationFn: () => sendKits({ to_warehouse_id: Number(to), kits: qty, note: note.trim() || undefined }),
    onSuccess: (d) => { onSent(d); setMsg({ ok: true, text: t("prod.send.done", { n: d.sent.total, w: dest }) }); setQty({}); setNote(""); },
    onError: (e) => setMsg({ ok: false, text: (e as Error).message }),
  });
  return (
    <div ref={box}>
      <Card title={t("prod.send.title")}>
        <div className="space-y-3 px-5 py-3">
          <p className="text-xs text-slate-500">{t("prod.send.hint")}</p>
          <div className="flex gap-1">
            {KIT_WH.filter((w) => w.id !== "1").map((w) => (
              <button key={w.id} onClick={() => setTo(w.id)} aria-pressed={to === w.id}
                className={`rounded-lg px-3 py-1 text-sm ${to === w.id ? "bg-slate-900 text-white" : "text-slate-600 ring-1 ring-slate-200"}`}>{t("prod.send.to", { w: w.name })}</button>
            ))}
          </div>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {stock.map((k) => {
              const n = qty[k.code] ?? 0, bad = n > at(k, "1");
              return (
                <label key={k.code} className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: k.colour ?? "#888" }} />
                  <span className="min-w-0 flex-1 truncate text-slate-800">{k.label || k.code}</span>
                  <span className={`text-xs tabular-nums ${bad ? "font-semibold text-red-700" : "text-slate-400"}`}>{`${KIT_WH[0].name} ${at(k, "1")} · ${dest} ${at(k, to)}`}</span>
                  <input type="number" min={0} value={n || ""} aria-label={t("prod.send.qtyFor", { k: k.label || k.code })}
                    onChange={(e) => { const v = Math.max(0, Math.round(Number(e.target.value) || 0)); setMsg(null);
                      setQty((q) => { const c = { ...q }; if (v) c[k.code] = v; else delete c[k.code]; return c; }); }}
                    className="w-16 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm" />
                </label>
              );
            })}
          </div>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wide text-slate-500">{t("prod.note")}</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t("prod.send.notePh")}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <p className={`text-sm ${over.length ? "text-red-700" : "text-slate-600"}`}>
            {total ? t("prod.send.total", { n: total, w: dest }) : t("prod.send.none")}
            {over.length > 0 && ` ${t("prod.send.over", { list: over.map((k) => `${k.label || k.code} : ${qty[k.code]} / ${at(k, "1")}`).join(" ; ") })}`}
          </p>
          <button disabled={!total || over.length > 0 || send.isPending} onClick={() => setAsking(true)}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {total ? t("prod.send.button", { w: dest, n: total }) : t("prod.send.buttonEmpty", { w: dest })}
          </button>
          {asking && (
            <Ask text={`${t("prod.send.ask", { n: total, w: dest })} ${Object.entries(qty).map(([c, n]) => `${n} × ${label(c)}`).join(" · ")}`}
              yes={t("prod.send.yes")} onYes={() => { setAsking(false); send.mutate(); }} onNo={() => setAsking(false)} />
          )}
          {msg && <p className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-800"}`}>{msg.text}</p>}
          <div>
            <p className="mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("prod.send.log")}</p>
            {log.length === 0 ? <p className="text-sm text-slate-400">{t("prod.send.logEmpty")}</p> : (
              <ul className="space-y-1 text-sm">
                {log.map((s, i) => (
                  <li key={i} className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-slate-700">
                      → {KIT_WH.find((w) => Number(w.id) === s.to)?.name ?? `#${s.to}`} · {t("prod.kitsMany", { n: s.total })} : {s.kits.map((k) => `${k.label || k.code} ×${k.qty}`).join(", ")}
                      {s.note ? ` — ${s.note}` : ""}{s.by ? ` (${s.by})` : ""}
                      {s.status === "in_transit" && <span className="ml-2 rounded bg-sky-50 px-1.5 text-xs text-sky-800">{t("prod.send.inTransit")}</span>}
                    </span>
                    <span className="text-xs text-slate-400">{zurichDateTime(s.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

// His shortage lines: what Dielsdorf and Berlin lack for their reservations, and what Penthalaz lacks
// for its own plus theirs, minus what is already being made — with "Prepare an order ×N".
function Shortages({ stock, onOrder, onTransfer }: {
  stock: StockRow[]; onOrder: (code: string, qty: number) => void; onTransfer: (to: string, code: string, qty: number) => void;
}) {
  const { t } = useT();
  const lines: { text: string; order?: { code: string; qty: number }; send?: { to: string; code: string; qty: number } }[] = [];
  KIT_WH.filter((w) => w.id !== "1").forEach((w) => stock.forEach((k) => {
    const s = k.warehouses?.[w.id];
    if (s && s.allocated > s.qty) lines.push({ text: t("prod.short.wh", { w: w.name, k: k.label || k.code, n: s.allocated - s.qty, a: s.allocated, s: s.qty }),
      send: { to: w.id, code: k.code, qty: s.allocated - s.qty } });
  }));
  stock.forEach((k) => {
    const pz = k.penthalaz;
    if (!pz || pz.need <= pz.on_hand) return;
    const missing = pz.need - pz.on_hand, inProd = k.in_production ?? 0, toMake = Math.max(0, missing - inProd);
    lines.push({
      text: t("prod.short.pz", { k: k.label || k.code, n: missing, a: pz.allocated, s: pz.on_hand,
        send: pz.to_send ? t("prod.short.send", { n: pz.to_send }) : "", prod: inProd ? t("prod.short.inProd", { n: inProd }) : "" }),
      order: toMake ? { code: k.code, qty: toMake } : undefined,
    });
  });
  if (!lines.length) return null;
  return (
    <ul className="space-y-1 border-b border-slate-100 px-5 py-2">
      {lines.map((l, i) => (
        <li key={i} className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-900">
          <span>{l.text}</span>
          {l.order && (
            <button onClick={() => onOrder(l.order!.code, l.order!.qty)} className="ml-auto rounded border border-red-300 bg-white px-2 py-0.5 text-xs">
              {t("prod.short.makeOrder", { n: l.order.qty })}
            </button>
          )}
          {l.send && (
            <button onClick={() => onTransfer(l.send!.to, l.send!.code, l.send!.qty)} className="ml-auto rounded border border-red-300 bg-white px-2 py-0.5 text-xs">
              {t("prod.short.makeTransfer", { n: l.send.qty })}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

// His reserved deliveries: by day (a past one waits for "Delivered"), each with its warehouse, type,
// contract and client, the kits in their colours and single articles with their names.
function AllocationList({ allocations, stock }: { allocations: Board["allocations"]; stock: StockRow[] }) {
  const { t, locale } = useT();
  const today = new Date().toISOString().slice(0, 10);
  const kits = new Map(stock.map((k) => [k.code, k]));
  const whName = (id: number | null) => KIT_WH.find((w) => Number(w.id) === id)?.name ?? (id ? `#${id}` : "—");
  const dayLabel = (d: string) => {
    const date = new Date(`${d}T12:00:00`);
    const wd = date.toLocaleDateString(locale, { weekday: "short" });
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1).replace(".", "")} ${d.split("-").reverse().join(".")}`;
  };
  if (!allocations.length) return <p className="px-5 py-3 text-sm text-slate-400">{t("prod.alloc.empty")}</p>;
  return (
    <div className="px-5 py-2">
      {allocations.map((a, i) => (
        <div key={a.id}>
          {a.needed_on && a.needed_on !== allocations[i - 1]?.needed_on && (
            <p className="mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {dayLabel(a.needed_on)}{a.needed_on < today ? t("prod.alloc.past") : ""}
            </p>
          )}
          <div className="rounded-lg px-2 py-1.5 hover:bg-slate-50">
            <div className="flex flex-wrap items-baseline gap-2 text-sm">
              <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-700">{whName(a.myotaku_warehouse_id)}</span>
              {a.contract_type && <span className="rounded bg-slate-50 px-1.5 text-xs text-slate-500 ring-1 ring-slate-200">{t(`prod.alloc.type.${a.contract_type}`)}</span>}
              {a.myotaku_contract_id && <span className="font-mono text-xs text-slate-500" title={a.report_line ?? undefined}>#{a.myotaku_contract_id}</span>}
              {a.client_name && <span className="font-medium text-slate-800">{a.client_name}</span>}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(a.kits).filter(([, n]) => Number(n) > 0).map(([code, n]) => (
                <span key={code} className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs ring-1 ring-slate-200">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: kits.get(code)?.colour ?? "#888" }} />
                  {kits.get(code)?.label || code} <b>×{n}</b>
                </span>
              ))}
              {Object.entries(a.articles ?? {}).filter(([, n]) => Number(n) > 0).sort().map(([code, n]) => (
                <span key={code} className="rounded-full bg-slate-50 px-2 py-0.5 text-xs ring-1 ring-slate-200"
                  title={a.myotaku_warehouse_id === 1 ? t("prod.alloc.artPz") : t("prod.alloc.artElse", { w: whName(a.myotaku_warehouse_id) })}>
                  {code} {articleName(code, locale) ?? ""} <b>×{n}</b>
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ⛔ TWO CARDS IN ONE, as his: the manager's view manages (assign, split, cancel a waiting order;
// delete a finished or cancelled one; see how a running one is going), the preparer's view works
// (start, pause, tick, finish). Neither shows the other's buttons.
function OrderRow({ order, busy, me, manager, onStart, onPause, onUnit, onFinish, onStop, onCheck, onValidate, onCancel, onTakeOver, onAssign, onSplit, onDelete }: {
  order: Order; busy: boolean; me: string; manager: boolean; onStart: () => void; onPause: () => void; onUnit: (d: number) => void; onFinish: () => void;
  onStop: (reason: string, detail: string) => void; onCheck: (ref: string, on: boolean) => void; onValidate: () => void;
  onCancel: () => void; onTakeOver: () => void; onAssign: (to: string) => void; onSplit: (qty: number, to: string) => void; onDelete: () => void;
}) {
  const { t } = useT();
  const same = (a: string | null, b: string) => (a ?? "").trim().toLowerCase() === b.trim().toLowerCase();
  // His `isLockedForMe`: a running or paused order belongs to its owner; others only take over.
  const locked = !manager && (order.status === "running" || order.status === "paused") && !!order.owner && !same(order.owner, me);
  const waiting = order.status === "queued";
  const closed = order.status === "finished" || order.status === "cancelled";
  const [deleting, setDeleting] = useState(false);
  const [panel, setPanel] = useState<null | "assign" | "split" | "takeOver" | "startAssigned">(null);
  const [assignTo, setAssignTo] = useState(order.assigned_to ?? "");
  const [splitQty, setSplitQty] = useState(Math.floor(order.qty / 2));
  const [splitTo, setSplitTo] = useState("");
  const start = () => (waiting && order.assigned_to && !same(order.assigned_to, me) ? setPanel("startAssigned") : onStart());
  const [stopping, setStopping] = useState(false);
  const pct = order.qty > 0 ? Math.round((order.done_count / order.qty) * 100) : 0;
  const list = order.checklist ?? [];
  // His "Terminer maintenant": finishing before every unit is made asks first, and says the unit in
  // progress gives its ticked articles back. Cancelling asks too. Asked on the card, not in a pop-up.
  const [confirming, setConfirming] = useState<null | "finish" | "cancel">(null);
  const finish = () => (order.done_count >= order.qty ? onFinish() : setConfirming("finish"));
  const paused = order.status === "paused";
  // His "Bravo": every unit asked for is made — only the correction and "Mark as done" are left.
  const allMade = order.status === "running" && order.done_count >= order.qty;
  return (
    <li className="px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-medium text-slate-800">{order.kit_label || order.kit_code} ×{order.qty}</span>
        <StatusBadge order={order} />
        <span className="text-sm tabular-nums text-slate-600">{order.done_count}/{order.qty}</span>
        {/* His "👤 Pour X" while the order is still to do or in hand. */}
        {order.assigned_to && !closed && <span className="text-xs text-slate-500">👤 {t("prod.for")} {order.assigned_to}</span>}
        {order.note && <span className="text-xs italic text-slate-500">« {order.note} »</span>}
        {/* Every button of the card writes: greyed for someone who only reads Production. */}
        <div className="ml-auto"><ReadOnlyArea module="production">
        {manager ? (
          <div className="flex items-center gap-1.5">
            {/* His manager actions: on a WAITING order cancel, assign, split; on a closed one, delete. */}
            {waiting && (
              <>
                <button onClick={() => setPanel("assign")} className="rounded border border-slate-300 px-2.5 py-1.5 text-xs">
                  👤 {order.assigned_to ? t("prod.changeAssign") : t("prod.assign")}
                </button>
                {order.qty > 1 && order.done_count === 0 && (
                  <button onClick={() => setPanel("split")} className="rounded border border-slate-300 px-2.5 py-1.5 text-xs">✂️ {t("prod.split")}</button>
                )}
                <button onClick={() => setConfirming("cancel")} disabled={busy} className="text-xs text-slate-500 underline">{t("unit.cancel")}</button>
              </>
            )}
            {closed && (
              <button onClick={() => (deleting ? onDelete() : setDeleting(true))} onBlur={() => setDeleting(false)}
                className={`rounded px-2.5 py-1.5 text-xs ${deleting ? "bg-red-700 font-semibold text-white" : "text-slate-500 ring-1 ring-slate-200"}`}>
                {deleting ? t("prod.deleteYes") : t("prod.delete")}
              </button>
            )}
          </div>
        ) : locked ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setPanel("takeOver")} className="rounded border border-slate-300 px-3 py-1.5 text-sm">{t("prod.takeOver")}</button>
          </div>
        ) : (
        <div className="flex items-center gap-1.5">
          {list.length === 0 && !waiting && (
            <>
              <button onClick={() => onUnit(-1)} disabled={busy} className="h-8 w-8 rounded border border-slate-300 text-slate-600">−</button>
              <button onClick={() => onUnit(1)} disabled={busy} className="h-8 w-10 rounded bg-emerald-600 font-bold text-white">+1</button>
            </>
          )}
          {order.status === "running" ? (
            !allMade && <button onClick={onPause} className="rounded border border-slate-300 px-3 py-1.5 text-sm">{t("run.pause")}</button>
          ) : !paused && (
            <button onClick={start} className="rounded bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white">{t("run.start")}</button>
          )}
          {!waiting && (
            <button onClick={finish} disabled={busy} className="rounded bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white">
              {order.done_count >= order.qty ? t("unit.markDone") : t("unit.finishNow")}
            </button>
          )}
          {order.status === "running" && !allMade && (
            <button onClick={() => setStopping(true)} className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-700">{t("prod.stop")}</button>
          )}
        </div>
        )}
        </ReadOnlyArea></div>
      </div>
      <OrderMeta order={order} />
      <OrderComposition order={order} />
      {/* His carton sheets, on a waiting, running or paused order, for the manager and the preparer. */}
      {!closed && <div className="mt-1.5"><CartonSheetsButton kitCode={order.kit_code} qty={order.qty} /></div>}
      {/* Below: the questions, the pause banner's buttons and the checklist — all writes. Printing the
          carton sheets above stays open to a reader. */}
      <ReadOnlyArea module="production">
      {locked && (
        <div className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
          🔒 <b>{t("prod.lockTitle")}</b>{" "}
          {t("prod.lockBody").replace(/\{o\}/g, order.owner ?? "").replace("{paused}", order.status === "paused" ? ` ${t("prod.pausedTag")}` : "")
            .replace("{d}", String(order.done_count)).replace("{q}", String(order.qty))}
        </div>
      )}
      {panel === "takeOver" && (
        <Ask text={`${t("prod.takeOverAsk").replace("{k}", `${order.kit_label || order.kit_code} ×${order.qty}`)} ${order.owner} → ${me}`}
          yes={t("prod.takeOverYes")} onYes={() => { setPanel(null); onTakeOver(); }} onNo={() => setPanel(null)} />
      )}
      {panel === "startAssigned" && (
        <Ask text={t("prod.assignedAsk").replace("{n}", order.assigned_to ?? "")} yes={t("prod.assignedYes")}
          onYes={() => { setPanel(null); onStart(); }} onNo={() => setPanel(null)} />
      )}
      {panel === "assign" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span>{t("prod.assignName")}</span>
          <input value={assignTo} onChange={(e) => setAssignTo(e.target.value)} list="prepNamesList" maxLength={40}
            className="w-48 rounded border border-slate-300 px-2 py-1" />
          <button onClick={() => { setPanel(null); onAssign(assignTo.trim()); }} className="rounded bg-slate-900 px-3 py-1 text-white">{t("prod.save")}</button>
          <button onClick={() => setPanel(null)} className="underline">{t("unit.no")}</button>
        </div>
      )}
      {panel === "split" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span>{t("prod.splitQty").replace("{n}", String(order.qty - 1))}</span>
          <input type="number" min={1} max={order.qty - 1} value={splitQty} onChange={(e) => setSplitQty(Number(e.target.value))}
            className="w-20 rounded border border-slate-300 px-2 py-1" />
          <span>{t("prod.splitTo")}</span>
          <input value={splitTo} onChange={(e) => setSplitTo(e.target.value)} list="prepNamesList" maxLength={40}
            className="w-40 rounded border border-slate-300 px-2 py-1" />
          <button onClick={() => { setPanel(null); onSplit(splitQty, splitTo.trim()); }}
            disabled={!(splitQty >= 1 && splitQty <= order.qty - 1)} className="rounded bg-slate-900 px-3 py-1 text-white disabled:opacity-40">{t("prod.splitYes")}</button>
          <button onClick={() => setPanel(null)} className="underline">{t("unit.no")}</button>
        </div>
      )}
      {confirming && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
          <span>{(confirming === "finish" ? t("unit.finishConfirm") : t("unit.cancelConfirm"))
            .replace("{d}", String(order.done_count)).replace("{q}", String(order.qty))}</span>
          <button onClick={() => { setConfirming(null); if (confirming === "finish") onFinish(); else onCancel(); }}
            className="rounded bg-amber-700 px-3 py-1 text-sm font-semibold text-white">{t("unit.yes")}</button>
          <button onClick={() => setConfirming(null)} className="text-sm underline">{t("unit.no")}</button>
        </div>
      )}
      {order.open_stop && (
        <p className="mt-1 text-xs text-red-700">{t("prod.stoppedAt", { t: clock(order.open_stop.stopped_at), n: order.open_stop.stopped_by || "—" })}{" "}
          {reasonLabel(order.open_stop.reason, t)}{order.open_stop.detail ? ` : ${order.open_stop.detail}` : ""}</p>
      )}
      <TimeCompare order={order} />
      {/* His manager's line on a running order: which unit and how many articles are ticked. */}
      {manager && order.status === "running" && list.length > 0 && (
        <p className="mt-1 text-xs text-slate-600">{t("prod.unitSummary", { n: Math.min(order.done_count + 1, order.qty), c: list.filter((r) => r.checked).length, m: list.length })}</p>
      )}
      {/* His banner on a paused order, for its owner: why, since when, and the way back. */}
      {!manager && paused && !locked && (
        <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${order.open_stop ? "bg-red-50 text-red-900 ring-1 ring-red-200" : "bg-amber-50 text-amber-900 ring-1 ring-amber-200"}`}>
          <b>{order.open_stop ? t("prod.stopBanner") : t("prod.pauseBanner")}</b>
          <div>{t("prod.pauseSince", { t: order.paused_at ? clock(order.paused_at) : "—", by: order.paused_by ? t("prod.pauseBy", { n: order.paused_by }) : "",
            d: order.done_count, q: order.qty })}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={start} disabled={busy} className="rounded bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white">{t("prod.resume")}</button>
            {!order.open_stop && (
              <button onClick={() => setStopping(true)} className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700">{t("prod.reportProblem")}</button>
            )}
          </div>
        </div>
      )}
      {!manager && allMade && !locked && (
        <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 ring-1 ring-emerald-200">
          {t(order.qty > 1 ? "prod.bravoMany" : "prod.bravoOne", { q: order.qty })}
        </p>
      )}
      {stopping && <StopForm order={order} onCancel={() => setStopping(false)}
        onSend={(reason, detail) => { setStopping(false); onStop(reason, detail); }} />}
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-600" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {/* His checklist is for a running order: a paused one shows its banner and the way back. */}
      {!manager && list.length > 0 && order.done_count < order.qty && !locked && order.status === "running" && (
        <UnitChecklistBox order={order} list={list} busy={busy} onCheck={onCheck} onValidate={onValidate} onCorrect={() => onUnit(-1)} />
      )}
      {!manager && !closed && list.length > 0 && order.done_count >= order.qty && order.done_count > 0 && !locked && (
        <button onClick={() => onUnit(-1)} disabled={busy} className="mt-2 text-xs text-slate-500 underline">
          {t("unit.correct").replace("{d}", String(order.done_count)).replace("{p}", String(order.done_count - 1))}
        </button>
      )}
      </ReadOnlyArea>
    </li>
  );
}

// His badge: the status, "Arrêté" for a paused order stopped by a problem, and "— owner" while in hand.
function StatusBadge({ order }: { order: Order }) {
  const { t } = useT();
  const key = order.status === "paused" && order.open_stop ? "stopped" : order.status;
  const tone: Record<string, string> = {
    queued: "bg-slate-100 text-slate-700", running: "bg-sky-100 text-sky-800", paused: "bg-amber-100 text-amber-900",
    stopped: "bg-red-100 text-red-800", finished: "bg-emerald-100 text-emerald-800", cancelled: "bg-slate-100 text-slate-500",
  };
  const inHand = (order.status === "running" || order.status === "paused") && order.owner;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone[key] ?? tone.queued}`}>
      {t(`prod.status.${key}`)}{inHand ? ` — ${order.owner}` : ""}
    </span>
  );
}

// His `order-composition`: a MUSA order's sub-kits as pills, from its checklist groups.
function OrderComposition({ order }: { order: Order }) {
  const groups = groupsOf(order.checklist ?? []).filter(([key]) => key && key !== order.kit_code);
  if (groups.length < 2) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {groups.map(([key, rows]) => (
        <span key={key} className="flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-xs ring-1 ring-slate-200">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: rows[0].group_color ?? "#888" }} />{rows[0].group_label ?? key}
        </span>
      ))}
    </div>
  );
}

// His meta line: when it was sent, the total time given, who started it and who finished it.
function OrderMeta({ order }: { order: Order }) {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const bits = [zurichDateTime(order.created_at), t("prod.meta.total", { t: hm(order.estimated_minutes) }),
    order.started_by && t("prod.meta.startedBy", { n: order.started_by }), order.finished_by && t("prod.meta.finishedBy", { n: order.finished_by })];
  return <p className="mt-0.5 text-xs text-slate-500">{bits.filter(Boolean).join(" · ")}</p>;
}

// His time line (`computeTimeCompare`): the time really spent against the time given (qty × min),
// green when within half a minute, amber when within 15 % or 5 min over, red beyond.
function TimeCompare({ order }: { order: Order }) {
  const { t } = useT();
  if (!["running", "paused", "finished"].includes(order.status) || !order.worked_minutes) return null;
  const actual = order.worked_minutes, given = order.estimated_minutes, delta = actual - given;
  const over = given > 0 ? delta / given : 0;
  const tone = delta <= 0.5 ? "text-emerald-700" : over <= 0.15 || Math.abs(delta) <= 5 ? "text-amber-700" : "text-red-700";
  const gap = Math.abs(delta) <= 0.5 ? t("prod.time.exact") : t(delta > 0 ? "prod.time.more" : "prod.time.less", { d: hm(Math.abs(delta)) });
  const vars = { a: hm(actual), g: hm(given), d: gap, at: order.paused_at ? clock(order.paused_at) : "—" };
  const key = order.status === "running" ? "prod.time.running" : order.status === "paused" ? "prod.time.paused" : "prod.time.done";
  return <p className={`mt-1 text-xs font-medium ${tone}`}>{t(key, vars)}</p>;
}

// His `fmtClock`: hh:mm, on the warehouses' clock wherever the reader is.
function clock(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" });
}

// His "Unité n° x/y": tick each article as it goes into the carton — it leaves the shelf as it is
// ticked — then validate the unit, which puts the kit in stock.
function UnitChecklistBox({ order, list, busy, onCheck, onValidate, onCorrect }: {
  order: Order; list: NonNullable<Order["checklist"]>; busy: boolean;
  onCheck: (ref: string, on: boolean) => void; onValidate: () => void; onCorrect: () => void;
}) {
  const { t, locale } = useT();
  const checked = list.filter((r) => r.checked).length;
  const all = checked === list.length;
  const next = Math.min(order.qty, order.done_count + 1);
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <b className="text-sm text-slate-800">{t("unit.title").replace("{n}", String(next)).replace("{q}", String(order.qty))}</b>
        <span className="text-xs text-slate-500">{t("unit.count").replace("{c}", String(checked)).replace("{m}", String(list.length))}</span>
      </div>
      {/* His groups: on a MUSA kit each sub-kit under its own coloured header; one kit, one group. */}
      {groupsOf(list).map(([key, rows]) => (
        <div key={key}>
          {groupsOf(list).length > 1 && (
            <p className="mt-2 mb-1 flex items-center gap-1.5 text-xs font-semibold" style={{ color: rows[0].group_color ?? undefined }}>
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: rows[0].group_color ?? "#888" }} />{rows[0].group_label ?? key}
            </p>
          )}
      <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {rows.map((r) => (
          <li key={r.reference}>
            <label className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${r.checked ? "bg-emerald-50 text-slate-500" : "text-slate-800"}`}>
              <input type="checkbox" checked={r.checked} disabled={busy} onChange={(e) => onCheck(r.reference, e.target.checked)} />
              <span className="w-16 font-mono text-xs">{r.reference}</span>
              <span className={`min-w-0 flex-1 truncate ${r.checked ? "line-through" : ""}`}>{articleName(r.reference, locale) ?? r.reference}</span>
              <span className={`text-xs ${r.per_unit > 1 ? "rounded bg-slate-200 px-1" : ""}`}>×{r.per_unit}</span>
              <span className={`w-20 text-right text-xs ${r.in_stock < r.per_unit && !r.checked ? "font-medium text-red-700" : "text-slate-400"}`}>
                {t("unit.inStock").replace("{n}", String(r.in_stock))}
              </span>
            </label>
          </li>
        ))}
      </ul>
        </div>
      ))}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={onValidate} disabled={!all || busy}
          className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
          {all ? t("unit.validate").replace("{d}", String(order.done_count)).replace("{n}", String(next)).replace("{q}", String(order.qty))
            : t("unit.tickAll").replace("{c}", String(checked)).replace("{m}", String(list.length))}
        </button>
        {order.done_count > 0 && (
          <button onClick={onCorrect} disabled={busy} className="text-xs text-slate-500 underline">
            {t("unit.correct").replace("{d}", String(order.done_count)).replace("{p}", String(order.done_count - 1))}
          </button>
        )}
      </div>
    </div>
  );
}

// His stock audit card: red with errors, amber with points to check, green when everything agrees.
// Checked again a moment after the board changes and every 10 minutes.
function AuditCard({ boardAt }: { boardAt: number }) {
  const { t } = useT();
  const { zurichDateTime } = useFormat();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["stock-audit"], queryFn: fetchAudit, refetchInterval: 600_000 });
  useEffect(() => {
    const id = setTimeout(() => void qc.invalidateQueries({ queryKey: ["stock-audit"] }), 2500);
    return () => clearTimeout(id);
  }, [boardAt, qc]);
  const issues = q.data?.issues ?? [];
  const errs = issues.filter((i) => i.level === "err").length, warns = issues.filter((i) => i.level === "warn").length;
  const tone = errs ? "border-red-300" : warns ? "border-amber-300" : "border-emerald-200";
  const fmt = (v: AuditIssue["vars"]) => {
    const out: Record<string, string | number> = {};
    Object.entries(v).forEach(([k, x]) => {
      out[k] = x == null ? "—" : k === "at" ? zurichDateTime(String(x)) : k === "day" ? String(x).split("-").reverse().slice(0, 2).join(".") : x;
    });
    if ("contract" in v) out.label = [out.day, out.warehouse, t("audit.contract", { n: out.contract }), v.client ? out.client : null].filter(Boolean).join(" · ");
    return out;
  };
  return (
    <section className={`overflow-hidden rounded-xl border-2 bg-white ${tone}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 bg-slate-50 px-5 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("audit.title")}</h2>
        {q.data && <span className="text-xs text-slate-400">{t("audit.checkedAt", { t: zurichDateTime(q.data.checked_at).split(" ").pop() ?? "" })}</span>}
      </div>
      <div className="px-5 py-3 text-sm">
        {!q.data ? <p className="text-slate-400">{q.isError ? (q.error as Error).message : t("audit.checking")}</p> : (
          <>
            <p className={`font-semibold ${errs ? "text-red-700" : warns ? "text-amber-700" : "text-emerald-700"}`}>
              {errs || warns ? [errs && t("audit.errs", { n: errs }), warns && t("audit.warns", { n: warns })].filter(Boolean).join(" · ") : t("audit.ok")}
            </p>
            {issues.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {issues.map((i, n) => {
                  const v = fmt(i.vars);
                  return (
                    <li key={n} className={`rounded-lg px-3 py-2 ${i.level === "err" ? "bg-red-50 text-red-900" : i.level === "warn" ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-700"}`}>
                      {t(`audit.${i.code}`, v)}
                      <small className="mt-0.5 block opacity-80">{t(`audit.${i.code}D`, v)}</small>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        <p className="mt-2 text-xs text-slate-400">{t("audit.checks")}</p>
      </div>
    </section>
  );
}

// The checklist rows by group, in his order.
function groupsOf(list: NonNullable<Order["checklist"]>) {
  const m = new Map<string, NonNullable<Order["checklist"]>>();
  list.forEach((r) => { const k = r.group ?? ""; m.set(k, [...(m.get(k) ?? []), r]); });
  return [...m.entries()];
}

// A yes/no asked on the card itself, as his confirm dialogs ask.
function Ask({ text, yes, onYes, onNo }: { text: string; yes: string; onYes: () => void; onNo: () => void }) {
  const { t } = useT();
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
      <span>{text}</span>
      <button onClick={onYes} className="rounded bg-amber-700 px-3 py-1 text-sm font-semibold text-white">{yes}</button>
      <button onClick={onNo} className="text-sm underline">{t("unit.no")}</button>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <h2 className="border-b border-slate-100 bg-slate-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Problem({ message }: { message: string }) {
  const { t } = useT();
  return <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800"><b>{t("error.title")}:</b> {message}</div>;
}

// His reasons are stored in French, as he wrote them; each language shows its own words.
function reasonLabel(reason: string, t: (k: string) => string) {
  const i = (STOP_REASONS as readonly string[]).indexOf(reason);
  return i >= 0 ? t(`prod.reason${i}`) : reason;
}

// "Arrêter la préparation et prévenir le responsable ?" — his dialog: a reason, and a detail that
// starts with the articles this warehouse has fewer of than one kit needs.
function StopForm({ order, onCancel, onSend }: { order: Order; onCancel: () => void; onSend: (reason: string, detail: string) => void }) {
  const { t } = useT();
  const [reason, setReason] = useState<string>(STOP_REASONS[0]);
  const [detail, setDetail] = useState("");
  // His prefill: the unit's articles this warehouse has fewer of than one unit needs.
  const [prefilled, setPrefilled] = useState(false);
  if (!prefilled) {
    const short = (order.checklist ?? []).filter((r) => r.in_stock < r.per_unit).map((r) => r.reference);
    if (short.length) setDetail(`${t("prod.stockShort")} : ${short.join(", ")}`);
    setPrefilled(true);
  }
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
      <p className="font-medium text-red-900">{t("prod.stopTitle")} — {order.kit_label || order.kit_code} ×{order.qty}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {STOP_REASONS.map((r, i) => (
          <label key={r} className="flex items-center gap-1.5">
            <input type="radio" name={`stop-${order.id}`} checked={reason === r} onChange={() => setReason(r)} />
            {t(`prod.reason${i}`)}
          </label>
        ))}
      </div>
      <textarea value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={300} rows={2}
        placeholder={t("prod.stopDetail")} className="w-full rounded border border-red-200 bg-white px-2 py-1" />
      <div className="flex gap-2">
        <button onClick={() => onSend(reason, detail)} className="rounded bg-red-700 px-3 py-1.5 font-semibold text-white">{t("prod.stopSend")}</button>
        <button onClick={onCancel} className="rounded border border-slate-300 bg-white px-3 py-1.5">{t("cancel")}</button>
      </div>
    </div>
  );
}

// The responsable's box: what is stopped right now, why, and since when.
function StoppedBox({ stops, onResolve }: { stops: Board["stopped"]; onResolve: (id: number) => void }) {
  const { t } = useT();
  const { dateTime } = useFormat();
  return (
    <div className="overflow-hidden rounded-xl border border-red-200 bg-red-50">
      <h2 className="border-b border-red-100 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-red-800">⛔ {t("prod.stoppedTitle")}</h2>
      <ul className="divide-y divide-red-100">
        {stops.map((st) => (
          <li key={st.id} className="px-5 py-2 text-sm text-red-900">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <b>{st.kit_label}</b>{st.qty ? ` ×${st.qty}` : ""} — {reasonLabel(st.reason, t)}{st.detail ? ` — ${st.detail}` : ""}
                <span className="text-red-700"> · {st.stopped_by} · {dateTime(st.stopped_at)} ({st.done_count ?? 0}/{st.qty} {t("prod.doneShort")})</span>
                {/* His notice when the alert could not go out: tell the responsable another way. */}
                {st.alert === "failed" && <span className="block text-xs font-semibold text-red-800">⚠️ {t("prod.alertFailed")}</span>}
              </div>
              <button onClick={() => onResolve(st.id)} className="rounded border border-red-300 bg-white px-2.5 py-1 text-xs font-semibold text-red-800">
                ✔ {t("prod.resolve")}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// His "📁 Documents & checklists" — every kit's carton checklist to print, and his reference PDFs.
function DocumentsCard() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-5 py-3 text-left">
        <span><b className="text-sm text-slate-900">📁 {t("docs.title")}</b><span className="block text-xs text-slate-500">{t("docs.hint")}</span></span>
        <span className="text-slate-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="border-t border-slate-100 bg-slate-50 p-4"><KitChecklists /></div>}
    </div>
  );
}

// His `dev-banner`, first on his page, both lines as he wrote them (PRD-001); his name in bold.
function DevBanner() {
  const { t } = useT();
  const name = "Ludovic Arsène René Lamoise";
  const line1 = t("prod.devBanner1").split("{name}");
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-center text-xs text-amber-900">
      <p>{line1.map((part, i) => <span key={i}>{part}{i < line1.length - 1 && <strong>{name}</strong>}</span>)}</p>
      <p className="mt-0.5">{t("prod.devBanner2")}</p>
    </div>
  );
}
